import {
  AssetGroups,
  AssetType,
  BaseAsset, CopySource,
  Quote,
  SecuritySymbol,
  SymbolGroup,
  SymbolType,
  Watchlist,
  WatchlistItem,
  WatchlistItemResponse,
  WatchlistResponse,
  WatchlistResponseHoldings,
  WatchlistTab
} from '../../domain/public-api';
import { TickerSymbolStatus } from '../../features/ticker-symbol/ticker-symbol.domain';
import { UtilityService } from '../../utils/utility.service';

export class WatchlistUtils {

  /**
   * Maximum number of items in a watchlist.
   */
  public static readonly MAX_WATCHLIST_ENTRIES: number = 10;

  /**
   * When tab is changed, it may cross the boundary between Recent Quotes and Watchlists, which have significant differences.
   */
  static toWatchlistTabType(selectedTab: string): WatchlistTab {
    return selectedTab !== undefined && Number(selectedTab) > 0 ?  WatchlistTab.WATCHLISTS : WatchlistTab.RECENT_QUOTES;
  }

  static getTotalNumberOfWatchlistItems(watchlists: Array<Watchlist>): number {
    let total = 0;
    if (WatchlistUtils.hasWatchlists(watchlists)) {
      watchlists.forEach( (watchlist: Watchlist): void => {
        total += WatchlistUtils.getNumberOfWatchlistItems(watchlist);
      });
    }
    return total;
  }

  /**
   * Adds a new symbol to watchlist.
   *
   * @param watchlist the watchlist container object
   * @param symbolToAdd the new symbol to add to the watchlist
   */
  static addToWatchlist(watchlist: Watchlist, symbolToAdd: SecuritySymbol): Watchlist {
    const symbolType = symbolToAdd.symbolType ?? SymbolType.STOCK;
    if (symbolToAdd.tickerSymbol === undefined && symbolToAdd.symbol !== undefined) {
      symbolToAdd.tickerSymbol = symbolToAdd.symbol;
    }
    if (watchlist.assetGroups[symbolType] === undefined) {
      watchlist.assetGroups[symbolType] = new Array<WatchlistItem>();
    }
    watchlist.assetGroups[symbolType].push(symbolToAdd);
    return watchlist;
  }

  /**
   * Removes editableFields content from all watchlist items in a watchlist.
   *
   * @param watchlist to trim
   * @returns trimmed watchlist
   */
  static deleteEditableFields(watchlist: Watchlist): Watchlist {
    const trimmedWatchlist: Watchlist = UtilityService.deepExtend({}, watchlist);
    if (trimmedWatchlist.assetGroups !== undefined) {
      if (trimmedWatchlist.assetGroups.ALL_SECURITY_TYPES !== undefined) {
        delete trimmedWatchlist.assetGroups.ALL_SECURITY_TYPES;
      }
      for (const watchlistType in trimmedWatchlist.assetGroups) {
        if (Object.getOwnPropertyDescriptor(trimmedWatchlist.assetGroups, watchlistType)) {
          const watchlistsByType: Array<WatchlistItem> = trimmedWatchlist.assetGroups[watchlistType];
          watchlistsByType.forEach((entry: WatchlistItem): void => {
            if (entry.editableFields !== undefined) {
              delete entry.editableFields;
            }
          });
        }
      }
    }
    return trimmedWatchlist;
  }

  // TODO: extract 'ALL_SECURITY_TYPES' back into handler?
  static removeItemFromWatchlist(watchlist: Watchlist, watchlistItem: WatchlistItem): void {
    const symbolsMatchFn = (item: WatchlistItem): boolean =>
      item.symbol.tickerSymbol === watchlistItem.symbol.tickerSymbol && item.symbol.market === watchlistItem.symbol.market;

    WatchlistUtils.watchlistIterator(watchlist, (items: Array<WatchlistItem>): void => {
      const index: number = items.findIndex(symbolsMatchFn);
      if (index >= 0) {
        items.splice(index, 1);
      }
    });
  }

  static hasWatchlists(watchlists: Array<Watchlist>): boolean {
    return Array.isArray(watchlists) && watchlists.length > 0;
  }

  /**
   * Checks if a symbol group (of type Watchlist or RecentQuotes) is not empty and has some items.
   *
   * @param symbolGroup - a container to check if it contains any items.
   * @returns true if a given container contains at least one item, false otherwise.
   */
  static watchlistHasItems<T extends BaseAsset, V extends SymbolGroup<T>>(symbolGroup: V): boolean {
    return WatchlistUtils
      .watchlistIterator(symbolGroup, (items: Array<WatchlistItem>): boolean => items !== undefined && items.length > 0) as boolean;
  }

  static getNumberOfWatchlistItems(watchlist: Watchlist): number {
    let num = 0;
    WatchlistUtils.watchlistIterator(watchlist, (items: Array<WatchlistItem>): void => {
      num += items.length; // WATCH FOR IMPLICIT JS RETURN VALUE
    });
    return num;
  }

  static watchlistIterator<T extends BaseAsset, V extends SymbolGroup<T>>(
    watchlist: V, itemHandler: (watchlistItems: Array<WatchlistItem>) => boolean | void): boolean | void {
    if (watchlist !== undefined && watchlist.assetGroups !== undefined) {
      for (const watchlistType in watchlist.assetGroups) {
        if (Object.getOwnPropertyDescriptor(watchlist.assetGroups, watchlistType) &&
          !WatchlistUtils.isEmpty(watchlist.assetGroups[watchlistType])
          && watchlistType !== 'ALL_SECURITY_TYPES') {
          const watchlistItems: Array<WatchlistItem> = watchlist.assetGroups[watchlistType];
          const result = itemHandler(watchlistItems);
          if (result !== undefined) {
            // terminate iterator if desired
            return result;
          }
        }
      }
    }
    return false;
  }

  /**
   * Checks if a watchlist contains more than or equal to {@link WatchlistService#MAX_WATCHLIST_ENTRIES} entries.
   *
   * @param watchlist - to check for capacity
   * @returns returns true if watchlist reached the maximum capacity, false otherwise
   */
  static isWatchlistAtMaxCapacity(watchlist: Watchlist): boolean {
    return watchlist !== undefined &&
      WatchlistUtils.getNumberOfWatchlistItems(watchlist) >= WatchlistUtils.MAX_WATCHLIST_ENTRIES;
  }

  /**
   * Checks if watchlist contains a given symbol.
   *
   * @param watchlist container
   * @param symbol to check against the watchlist container
   * @returns true if watchlist contains the symbol, false otherwise.
   */
  static hasSymbol(watchlist: Watchlist, symbol: SecuritySymbol): boolean {
    if (!this.watchlistHasItems(watchlist)) {
      return false;
    }
    const watchlistValid = watchlist !== undefined && watchlist.assetGroups !== undefined;
    const symbolValid = symbol !== undefined && symbol.market !== undefined
      && (symbol.tickerSymbol !== undefined || symbol.displaySymbol !== undefined || symbol.symbol !== undefined);

    const inputSymbol = symbolValid ? WatchlistUtils.extractInputSymbol(symbol) : undefined;

    if (watchlistValid && symbolValid && inputSymbol !== undefined) {
      for (const type in watchlist.assetGroups) {
        if (Object.getOwnPropertyDescriptor(watchlist.assetGroups, type)
          && Object.keys(AssetType).find((assetType: string): boolean => assetType === type) !== undefined
          && WatchlistUtils.symbolGroupContainsSymbol(watchlist.assetGroups[type], inputSymbol, symbol.market)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Custom shallow copy of the top level Watchlist construct which:
   *   filters unwanted properties;
   *   trims whitespace from string typed properties.
   */

  static copy<T>(source: CopySource, propertiesToExclude: Array<string>): T {
    const result: T = {} as T;
    const propertyFilter = (propertyName: string): boolean => propertiesToExclude.indexOf(propertyName) === -1;
    for (const prop in source) {
      if (Object.getOwnPropertyDescriptor(source, prop) && propertyFilter(prop) && source[prop] !== undefined) {
        const value = source[prop];
        result[prop] = (typeof value === 'string') ? value.trim() : value;
      }
    }
    return result;
  }

  static toSymbolStatus(quoteResponse:
  Pick<Quote, 'exchangeAgreementDeclined' | 'securityHalted' | 'quoteError' | 'symbolError'>,
                        symbolResponse: Pick<SecuritySymbol, 'symbolError' | 'type' | 'optionExpired'>): TickerSymbolStatus {
    if (quoteResponse.exchangeAgreementDeclined) {
      return TickerSymbolStatus.UNSIGNED_AGREEMENT;
    }

    if (quoteResponse.securityHalted) {
      return TickerSymbolStatus.HALTED;
    }

    if (symbolResponse.symbolError
      || (symbolResponse !== undefined && symbolResponse.type === SymbolType.OPTION && symbolResponse.optionExpired)
      || (symbolResponse !== undefined && symbolResponse.symbolError)) {
      return TickerSymbolStatus.INVALID;
    }
    if (quoteResponse.quoteError || quoteResponse.symbolError) {
      return TickerSymbolStatus.QUOTE_ERROR;
    }
    return undefined;
  }

  // TODO: move to mapper service
  static toWatchlistResponse(watchlist: Watchlist): WatchlistResponse {
    const trimmedWatchlist: Watchlist = WatchlistUtils.deleteEditableFields(watchlist);
    const watchlistToSave: WatchlistResponse = WatchlistUtils.copy(trimmedWatchlist, ['assetGroups', 'hasFundamentals']);

    if (trimmedWatchlist.assetGroups !== undefined) {
      watchlistToSave.holdings = {} as WatchlistResponseHoldings;
      for (const type in trimmedWatchlist.assetGroups) {
        if (Object.getOwnPropertyDescriptor(trimmedWatchlist.assetGroups, type)) {
          const items: Array<WatchlistItem> = trimmedWatchlist.assetGroups[type];
          if (items !== undefined && items.length > 0) {
            watchlistToSave.holdings[type] = WatchlistUtils.convertWatchlistItemsToFlat(items);
          }
        }
      }
    }
    return watchlistToSave;
  }

  static convertWatchlistItemsToFlat(originalWatchlistItems: Array<WatchlistItem>): Array<WatchlistItemResponse> {
    const watchlistItems: Array<WatchlistItemResponse> = [];
    if (originalWatchlistItems !== undefined) {
      originalWatchlistItems.forEach((originalItem: WatchlistItem) => {
        if (originalItem !== undefined) {
          const item: WatchlistItemResponse = this.convertEntryToFlat(originalItem);
          watchlistItems.push(item);
        }
      });
    }
    return watchlistItems;
  }

  static convertEntryToFlat(originalWatchlistItem: WatchlistItem): WatchlistItemResponse {
    const watchlistItem: WatchlistItemResponse = WatchlistUtils.copy<WatchlistItemResponse>(originalWatchlistItem, ['symbol', 'quote']);
    if (originalWatchlistItem.symbol !== undefined) {
      UtilityService.deepExtend(watchlistItem, originalWatchlistItem.symbol);
    }
    if (originalWatchlistItem.quote !== undefined) {
      UtilityService.deepExtend(watchlistItem, originalWatchlistItem.quote);
    }
    return watchlistItem;
  }

  static sortWatchlists(watchlists: Array<Watchlist>): Array<Watchlist> {
    if (watchlists === undefined || UtilityService.isEmptyCollection(watchlists)) {
      return watchlists;
    }
    return WatchlistUtils.sortWatchlistsByPushingEmptiesLast(WatchlistUtils.sortByName(watchlists));
  }

  static extractInputSymbol(symbol: SecuritySymbol): string {
    return [
      symbol.tickerSymbol,
      symbol.displaySymbol,
      symbol.symbol
    ].reduce((prev: string, next: string): string => prev ? prev.toLowerCase() : next ? next.toLowerCase() : undefined);
  }

  // public access is to make it testable
  // Note: Top Mover's terse quote symbols (aka watchlist items) come in bare bones form: {tickerSymbol: "TD", market: "CA"}
  static symbolGroupContainsSymbol(symbolGroup: Array<WatchlistItem>, inputSymbol: string, inputMarket: string): boolean {
    if (inputMarket !== undefined) {
      for (const watchlistItem of symbolGroup) {
        if (watchlistItem === undefined) {
          continue;
        }

        const watchlistItemMarket: string = (watchlistItem.symbol !== undefined && watchlistItem.symbol.market !== undefined)
          ? watchlistItem.symbol.market : watchlistItem.market;
        if (watchlistItemMarket !== inputMarket) {
          continue;
        }

        const watchlistItemDisplaySymbol: string = (watchlistItem.symbol !== undefined && watchlistItem.symbol.displaySymbol !== undefined)
          ? watchlistItem.symbol.displaySymbol.toLowerCase() : undefined;
        if (watchlistItemDisplaySymbol === inputSymbol) {
          return true;
        }

        const watchlistItemTickerSymbol: string = (watchlistItem.symbol !== undefined && watchlistItem.symbol.tickerSymbol !== undefined)
          ? watchlistItem.symbol.tickerSymbol : watchlistItem.tickerSymbol;
        if (watchlistItemTickerSymbol === 'OPTION') {
          const optionTicker = watchlistItemTickerSymbol.toLowerCase() + ' ' + watchlistItem.symbol.market.toLowerCase();
          if (optionTicker === inputSymbol) {
            return true;
          }
        }
        if (watchlistItemTickerSymbol !== undefined && watchlistItemTickerSymbol.toLowerCase() === inputSymbol) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Push watchlists with no items to the last of array
   *
   * @param watchlists Array of watchlists
   * @returns Returns array of watchlists with empty watchlists pushed to the end
   */
  private static sortWatchlistsByPushingEmptiesLast(watchlists: Array<Watchlist>): Array<Watchlist> {
    const nonEmptyLists: Array<Watchlist> = [];
    const emptyLists: Array<Watchlist> = [];

    watchlists.forEach((watchlist: Watchlist): void => {
      if (WatchlistUtils.getNumberOfWatchlistItems(watchlist) === 0) {
        emptyLists.push(watchlist);
      } else {
        nonEmptyLists.push(watchlist);
      }
    });
    return nonEmptyLists.concat(emptyLists);
  }

  /**
   * Sort watchlists by name
   *
   * @param watchlists Array of watchlists
   * @returns Returns array of watchlists sorted by name
   */
  private static sortByName(watchlists: Array<Watchlist>): Array<Watchlist> {
    return watchlists.sort(WatchlistUtils.compareByName);
  }

  /**
   * Compare function for sorting watchlists by name
   *
   * @param a Watchlist 1
   * @param b Watchlist 2
   * @returns -1 if a follows b, +1 if b follows a, 0 if they are equal
   */
  private static compareByName(a: Watchlist, b: Watchlist): number {
    if (a.name === undefined || b.name === undefined) {
      return 0;
    }
    const nameA = a.name.toLowerCase();
    const nameB = b.name.toLowerCase();
    return (nameA < nameB) ? -1
      : (nameA > nameB) ? 1
        : 0;
  }

  /**
   * Checks if a value is empty
   *
   * @param value value
   * @returns Returns true if value is empty. Otherwise, false.
   */
  private static isEmpty(value: AssetGroups<BaseAsset>): boolean {
    if (value === undefined || value === null) {
      return true;
    }
    if (Boolean(value) === false) {
      return true;
    }
    if (value instanceof Array || typeof value === 'string') {
      return value.length === 0;
    }
    for (const key in value) {
      if (Object.hasOwnProperty.call(value, key)) {
        return false;
      }
    }
    return true;
  }
}
